import React, { useEffect, useState } from "react";

import SvgBriefcase01 from "@uui/Briefcase01";
import SvgUser01 from "@uui/User01";
import SvgXClose from "@uui/XClose";
import { compact, remove } from "lodash/fp";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";

import useSearchMeetingsStore from "@app/analytics/hooks/meetings/useSearchMeetingsStore";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import {
  EMPTY_FILTER,
  RollupFilterOption,
} from "@app/analytics/providers/RollupContext";
import { useAuthenticated } from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useMeasure from "@app/common/hooks/useMeasure";
import ROUTES from "@app/common/routes";
import { withQueryParams } from "@app/common/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Stack from "@rui/Stack";
import { roundedOutline } from "@rui/styles";

import SearchPopover from "./SearchPopover";
import { SearchChip } from "./styles";
import { SearchChipProps } from "./types";
import { getTeamFilterOptions } from "../RollupFilter/RollupFilter";
import SearchBox from "../SearchBox";

const SEARCH_BAR_SMALL_BREAKPOINT = 162;

const getChips = (
  teamFilter: RollupFilterOption,
  usersFilter: RollupFilterOption,
  justMe: boolean,
  canAccessTeamRollupReports: boolean
) => {
  const teamChip =
    teamFilter.id === "ALL" || !canAccessTeamRollupReports
      ? null
      : {
          ...teamFilter,
          icon: <SvgBriefcase01 />,
          type: "team" as const,
        };

  const userChip = justMe
    ? null
    : {
        ...usersFilter,
        icon: <SvgUser01 />,
        type: "user" as const,
      };
  return compact([teamChip, userChip]);
};

const SearchBar = () => {
  const {
    teamFilter,
    setTeamFilter,
    usersFilter,
    setUsersFilter,
    filterBy,
    isLoading,
  } = useRollupContext();
  const { inWorkspace, workspace } = useWorkspace();
  const { user } = useAuthenticated();

  const { canAccessTeamRollupReports, canAccessWorkspaceRollupReports } =
    useFeatureAccess();

  const teamsFilterOptions = getTeamFilterOptions(
    canAccessWorkspaceRollupReports,
    workspace.teams
  );

  const defaultTeamsFilterOption = teamsFilterOptions[0];

  const justMe = filterBy === "me";

  const [chips, setChips] = useState<SearchChipProps[] | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTermFromParams = searchParams.get("q");

  const isSearching = useSearchMeetingsStore((state) => state.isSearching);

  const [searchTerm, setSearchTerm] = useState(searchTermFromParams ?? "");

  const location = useLocation();
  const navigate = useNavigate();

  const [measureRef, { width: elWidth }] = useMeasure<HTMLElement>();

  const isOnSearchResults = location.pathname.includes(
    ROUTES.MEETING_REPORTS_SEARCH
  );

  useEffect(() => {
    // clear search term when going back to the meeting reports page
    if (!isOnSearchResults) handleClear();
  }, [isOnSearchResults]);

  useEffect(() => {
    if (isLoading || !inWorkspace) return;
    // if user's default rollup is selected, do not show any chips
    if (
      justMe &&
      teamFilter.id === (defaultTeamsFilterOption ?? EMPTY_FILTER).id
    ) {
      setChips(null);
      return;
    }
    const newChips = getChips(
      teamFilter,
      usersFilter,
      justMe,
      canAccessTeamRollupReports
    );
    setChips(newChips);
  }, [filterBy, usersFilter, teamFilter, isLoading, inWorkspace]);

  const {
    anchorElement,
    onClick: onSearchExpanded,
    onClose: closePopover,
    open,
  } = useAnchorElement();

  const handleBlur = () => {
    closePopover();
  };

  const handleClear = () => {
    setSearchTerm("");
    closePopover();
    if (isOnSearchResults) {
      navigate(ROUTES.MEETING_REPORTS);
    }
  };

  const handleRemoveChip = (chip: SearchChipProps) => {
    if (chips === null) return;
    const newChips = remove({ id: chip.id }, chips);
    if (chip.type === "team") {
      // if user can access all teams, this will be "ALL TEAMS"
      // otherwise it'll reset to a team the user is on
      setTeamFilter(defaultTeamsFilterOption);
    }
    if (chip.type === "user") {
      setUsersFilter({ id: user.id, label: user.fullName });
    }
    setChips(newChips);

    closePopover();
  };

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    onSearchExpanded(e as any as React.MouseEvent<HTMLDivElement>);
  };

  const handleEnter = () => {
    if (isOnSearchResults) {
      setSearchParams((prev) => ({
        ...Object.fromEntries(prev.entries()),
        q: searchTerm,
      }));
    } else {
      navigate(
        withQueryParams(ROUTES.MEETING_REPORTS_SEARCH, { q: searchTerm })
      );
    }

    closePopover();
  };

  return (
    <Stack
      onFocus={handleFocus}
      onBlur={handleBlur}
      ref={measureRef}
      title={
        chips
          ? `Filtering by: ${chips.map((chip) => chip.label).join(", ")}`
          : undefined
      }
      sx={{ width: "100%", maxWidth: "600px" }}
    >
      <SearchBox
        isSearching={isSearching}
        onChange={setSearchTerm}
        onClear={handleClear}
        onEnter={handleEnter}
        placeholder={
          elWidth < SEARCH_BAR_SMALL_BREAKPOINT
            ? "Search"
            : "Search meetings..."
        }
        value={searchTerm}
        sx={{
          backgroundColor: searchTerm ? "#fff" : undefined,
          borderRadius: "8px",
          paddingX: 1,
          paddingY: 0.5,
          minWidth: "fit-content",
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.02)",
          "&:focus-within": {
            ...roundedOutline({
              borderRadius: "8px",
              border: "2px solid",
              borderColor: "shades.secondary.600",
            }),
          },
        }}
        chips={chips?.map((chip) => (
          <SearchChip
            variant="outlined"
            key={`${chip.type}-${chip.id}`}
            {...chip}
            onDelete={() => handleRemoveChip(chip)}
            deleteIcon={
              <SvgXClose
                className="search_chip__delete_icon"
                sx={{ height: "15px", color: "shades.neutral.600" }}
              />
            }
            sx={{
              ["@media (max-width:1018px)"]: {
                display: "none",
              },
            }}
          />
        ))}
      />
      <SearchPopover
        open={open}
        anchorEl={anchorElement}
        onClose={closePopover}
        sx={{ width: elWidth, minWidth: "300px" }}
      />
    </Stack>
  );
};

export default SearchBar;
